import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Download, Search, Check, FlaskConical, Trash2 } from "lucide-react";
import { CRM } from "../../config/content";
import { getLeads, updateLead, bulkUpdateLeads, downloadLeadsXlsx, seedTestLeads, deleteTestLeads, leadWhatsAppLink } from "../../lib/api";
import LeadDrawer from "./LeadDrawer";

const STATUS_STYLE = {
  hot: "text-[#EF4444] border-[#EF4444]/40 bg-[#EF4444]/10",
  warm: "text-[#FACC15] border-[#FACC15]/40 bg-[#FACC15]/10",
  cold: "text-slate-400 border-slate-500/40 bg-slate-500/10",
};
const STAGE_TONE_STYLE = {
  positive: "text-[#22C55E] border-[#22C55E]/40 bg-[#22C55E]/10",
  progress: "text-[#FACC15] border-[#FACC15]/40 bg-[#FACC15]/10",
  lost: "text-[#EF4444] border-[#EF4444]/40 bg-[#EF4444]/10",
  neutral: "text-[#A7B3AA] border-white/15 bg-white/5",
};
const stageTone = (stage) => CRM.stages.find((s) => s.id === stage)?.tone || "neutral";

const todayIST = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

function InlineSelect({ value, onChange, options, className, testid }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      data-testid={testid}
      className={`rounded-full border text-xs font-bold px-2.5 py-1.5 outline-none cursor-pointer appearance-none ${className}`}
      style={{ backgroundColor: "#0F1712" }}
    >
      {options.map((o) => (
        <option key={o.id} value={o.id} style={{ backgroundColor: "#0F1712", color: "#fff" }}>{o.label}</option>
      ))}
    </select>
  );
}

function NoteCell({ lead, onSave }) {
  const [val, setVal] = useState(lead.notes || "");
  const [saved, setSaved] = useState(false);
  useEffect(() => { setVal(lead.notes || ""); }, [lead.id, lead.notes]);
  return (
    <div className="relative min-w-[160px]" onClick={(e) => e.stopPropagation()}>
      <input
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={async () => {
          if (val !== (lead.notes || "")) {
            try {
              await onSave(lead.id, { notes: val });
              setSaved(true);
              setTimeout(() => setSaved(false), 1600);
            } catch (e) { toast.error(e.message); }
          }
        }}
        placeholder="Add note"
        data-testid={`note-input-${lead.id}`}
        className="w-full rounded-lg bg-[#070A08] border border-[#22C55E]/[0.15] focus:border-[#22C55E]/50 outline-none px-2.5 py-1.5 text-xs text-white placeholder:text-[#6B7A70] transition-colors"
      />
      {saved && (
        <span className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 text-[10px] font-bold text-[#22C55E]" data-testid={`note-saved-${lead.id}`}>
          <Check className="w-3 h-3" /> Saved
        </span>
      )}
    </div>
  );
}

const EMPTY_FILTERS = { q: "", status: "", stage: "", experience: "", problem: "", mode: "", source: "", from: "", to: "", due: false, partials: "all" };

export default function LeadsView() {
  const [leads, setLeads] = useState(null);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [selected, setSelected] = useState(() => new Set());
  const [drawerLead, setDrawerLead] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [bulkStage, setBulkStage] = useState("");
  const [bulkStatus, setBulkStatus] = useState("");
  const prevCount = useRef(null);

  const load = useCallback(async (silent = true) => {
    if (!silent) setRefreshing(true);
    try {
      const data = await getLeads();
      if (prevCount.current !== null && data.length > prevCount.current) {
        const newest = data[0];
        toast.success(`New lead: ${newest.name || "Someone"} ${newest.phone ? `(+91 ${newest.phone})` : ""}`);
      }
      prevCount.current = data.length;
      setLeads(data);
      setError("");
    } catch (e) {
      setError(e.message);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load(false);
    const iv = setInterval(() => load(true), 45000);
    const onVis = () => { if (!document.hidden) load(true); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
  }, [load]);

  const updateLocal = (updated) => {
    setLeads((ls) => (ls || []).map((l) => (l.id === updated.id ? updated : l)));
    setDrawerLead((d) => (d && d.id === updated.id ? updated : d));
  };

  const patch = async (id, p) => {
    const updated = await updateLead(id, p);
    updateLocal(updated);
    return updated;
  };

  const filtered = useMemo(() => {
    const list = leads || [];
    const today = todayIST();
    return list.filter((l) => {
      if (filters.partials === "hide" && l.form_state === "partial") return false;
      if (filters.partials === "only" && l.form_state !== "partial") return false;
      if (filters.status && l.status !== filters.status) return false;
      if (filters.stage && l.stage !== filters.stage) return false;
      if (filters.experience && l.experience !== filters.experience) return false;
      if (filters.problem && !(l.problems || []).includes(filters.problem)) return false;
      if (filters.mode && l.mode !== filters.mode) return false;
      if (filters.source) {
        const s = l.utm_source || "Direct";
        if (s !== filters.source) return false;
      }
      if (filters.due && !(l.next_follow_up && l.next_follow_up <= today)) return false;
      if (filters.from || filters.to) {
        const d = (l.created_at || "").slice(0, 10);
        if (filters.from && d < filters.from) return false;
        if (filters.to && d > filters.to) return false;
      }
      if (filters.q) {
        const s = filters.q.toLowerCase();
        if (![l.name, l.phone, l.email, l.city].join(" ").toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [leads, filters]);

  const options = useMemo(() => {
    const list = leads || [];
    const uniq = (fn) => [...new Set(list.flatMap(fn).filter(Boolean))];
    return {
      experience: uniq((l) => [l.experience]),
      problem: uniq((l) => l.problems || []),
      mode: uniq((l) => [l.mode]),
      source: uniq((l) => [l.utm_source || "Direct"]),
    };
  }, [leads]);

  const hasTestData = (leads || []).some((l) => l.test_data);

  const toggleSelect = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const applyBulk = async () => {
    if (!bulkStage && !bulkStatus) return;
    try {
      await bulkUpdateLeads([...selected], { stage: bulkStage || undefined, status: bulkStatus || undefined });
      toast.success(`Updated ${selected.size} lead${selected.size > 1 ? "s" : ""}`);
      setSelected(new Set());
      setBulkStage("");
      setBulkStatus("");
      load(true);
    } catch (e) { toast.error(e.message); }
  };

  const onExport = async () => {
    setExporting(true);
    try {
      await downloadLeadsXlsx(filtered.map((l) => l.id));
      toast.success("Excel file downloaded");
    } catch (e) { toast.error(e.message); } finally { setExporting(false); }
  };

  const onSeed = async () => {
    try {
      const r = await seedTestLeads();
      toast.success(r.created ? `Added ${r.created} test leads` : "Test leads already exist");
      load(true);
    } catch (e) { toast.error(e.message); }
  };

  const onDeleteTest = async () => {
    try {
      const r = await deleteTestLeads();
      toast.success(`Deleted ${r.deleted} test lead${r.deleted === 1 ? "" : "s"}`);
      load(true);
    } catch (e) { toast.error(e.message); }
  };

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const selectCls = "rounded-xl bg-[#070A08] border border-[#22C55E]/[0.18] focus:border-[#22C55E]/60 outline-none px-3 py-2 text-xs sm:text-sm text-[#A7B3AA] transition-colors";
  const today = todayIST();

  return (
    <div data-testid="leads-view">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="font-heading uppercase text-3xl sm:text-4xl tracking-wide text-white">Leads</h1>
          <p className="mt-0.5 text-sm text-[#6B7A70]" data-testid="leads-count">
            {leads ? `Showing ${filtered.length} of ${leads.length} leads` : "Loading..."}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!hasTestData && (
            <button onClick={onSeed} data-testid="seed-test-leads-button" className="inline-flex items-center gap-2 rounded-full border border-[#FACC15]/40 text-[#FACC15] text-xs font-bold px-4 py-2 hover:bg-[#FACC15]/10 transition-colors">
              <FlaskConical className="w-3.5 h-3.5" /> Add 10 test leads
            </button>
          )}
          {hasTestData && (
            <button onClick={onDeleteTest} data-testid="delete-test-leads-button" className="inline-flex items-center gap-2 rounded-full border border-[#EF4444]/40 text-[#EF4444] text-xs font-bold px-4 py-2 hover:bg-[#EF4444]/10 transition-colors">
              <Trash2 className="w-3.5 h-3.5" /> Delete test data
            </button>
          )}
          <button onClick={() => load(false)} data-testid="leads-refresh-button" className="inline-flex items-center gap-2 rounded-full border border-[#22C55E]/30 text-[#A7B3AA] hover:text-white px-4 py-2 text-xs font-bold transition-colors">
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button onClick={onExport} disabled={exporting || !filtered.length} data-testid="leads-export-button" className="inline-flex items-center gap-2 rounded-full bg-[#22C55E] hover:bg-[#16A34A] disabled:opacity-50 text-[#04100A] text-xs font-extrabold px-4 py-2 transition-colors">
            <Download className="w-3.5 h-3.5" /> Export Excel
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="leads-filters">
        <div className="flex items-center gap-2 rounded-xl bg-[#070A08] border border-[#22C55E]/[0.18] px-3 py-2 flex-1 min-w-[180px]">
          <Search className="w-4 h-4 text-[#6B7A70] shrink-0" />
          <input value={filters.q} onChange={(e) => set("q", e.target.value)} placeholder="Search name, phone, email, city" data-testid="leads-search-input" className="w-full bg-transparent outline-none text-sm text-white placeholder:text-[#6B7A70]" />
        </div>
        <select value={filters.status} onChange={(e) => set("status", e.target.value)} className={selectCls} data-testid="filter-status" aria-label="Status">
          <option value="">Status</option>
          {CRM.statuses.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <select value={filters.stage} onChange={(e) => set("stage", e.target.value)} className={selectCls} data-testid="filter-stage" aria-label="Stage">
          <option value="">Stage</option>
          {CRM.stages.map((s) => <option key={s.id} value={s.id}>{s.id}</option>)}
        </select>
        <select value={filters.experience} onChange={(e) => set("experience", e.target.value)} className={selectCls} data-testid="filter-experience" aria-label="Experience">
          <option value="">Experience</option>
          {options.experience.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <select value={filters.problem} onChange={(e) => set("problem", e.target.value)} className={selectCls} data-testid="filter-problem" aria-label="Problem">
          <option value="">Problem</option>
          {options.problem.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <select value={filters.mode} onChange={(e) => set("mode", e.target.value)} className={selectCls} data-testid="filter-mode" aria-label="Mode">
          <option value="">Mode</option>
          {options.mode.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <select value={filters.source} onChange={(e) => set("source", e.target.value)} className={selectCls} data-testid="filter-source" aria-label="Source">
          <option value="">Source</option>
          {options.source.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <input type="date" value={filters.from} onChange={(e) => set("from", e.target.value)} className={selectCls} data-testid="filter-from" aria-label="From date" />
        <input type="date" value={filters.to} onChange={(e) => set("to", e.target.value)} className={selectCls} data-testid="filter-to" aria-label="To date" />
        <button
          onClick={() => set("due", !filters.due)}
          data-testid="filter-due-today"
          className={`rounded-full text-xs font-bold px-4 py-2 border transition-colors ${filters.due ? "bg-[#FACC15]/15 text-[#FACC15] border-[#FACC15]/50" : "text-[#A7B3AA] border-[#22C55E]/[0.18] hover:border-[#FACC15]/40"}`}
        >
          Follow-ups due today
        </button>
        <select value={filters.partials} onChange={(e) => set("partials", e.target.value)} className={selectCls} data-testid="filter-partials" aria-label="Partial leads">
          <option value="all">All leads</option>
          <option value="hide">Hide partial</option>
          <option value="only">Only partial</option>
        </select>
      </div>

      {selected.size > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-2xl border border-[#FACC15]/30 bg-[#FACC15]/[0.06] px-4 py-3" data-testid="bulk-actions-bar">
          <span className="text-xs font-bold text-[#FACC15]">{selected.size} selected</span>
          <select value={bulkStage} onChange={(e) => setBulkStage(e.target.value)} className={selectCls} data-testid="bulk-stage-select" aria-label="Bulk stage">
            <option value="">Set stage…</option>
            {CRM.stages.map((s) => <option key={s.id} value={s.id}>{s.id}</option>)}
          </select>
          <select value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value)} className={selectCls} data-testid="bulk-status-select" aria-label="Bulk status">
            <option value="">Set status…</option>
            {CRM.statuses.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <button onClick={applyBulk} data-testid="bulk-apply-button" className="rounded-full bg-[#22C55E] text-[#04100A] text-xs font-extrabold px-4 py-2">Apply</button>
          <button onClick={() => setSelected(new Set())} data-testid="bulk-clear-button" className="text-xs text-[#A7B3AA] hover:text-white">Clear</button>
        </div>
      )}

      {error && <p className="mb-4 text-sm text-[#EF4444]" data-testid="leads-error">{error}</p>}

      {!leads ? (
        <div className="space-y-2" data-testid="leads-loading">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-14 rounded-xl bg-[#0F1712] border border-[#22C55E]/[0.08] animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-[#22C55E]/[0.18] bg-[#0F1712] p-12 text-center" data-testid="leads-empty">
          <p className="font-heading uppercase text-2xl text-white tracking-wide">No leads found</p>
          <p className="mt-2 text-sm text-[#6B7A70]">Try changing the filters, or wait for the next form submission.</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-[#22C55E]/[0.18] overflow-hidden" data-testid="leads-table">
          <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
            <table className="w-full text-sm border-separate border-spacing-0">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-[#6B7A70]">
                  {["", "Name", "Created", "Mobile", "Email", "City", "Experience", "Biggest Problems", "Markets", "Mode", "Timing", "Capital", "Source", "Status", "Stage", "Follow-up", "Notes", "Actions"].map((h, i) => (
                    <th key={i} className={`px-3 py-3 bg-[#0F1712] sticky top-0 z-20 border-b border-[#17231B] whitespace-nowrap ${i === 1 ? "left-0 z-30" : ""}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((l, idx) => {
                  const overdue = l.next_follow_up && l.next_follow_up < today;
                  const dueToday = l.next_follow_up === today;
                  return (
                    <tr
                      key={l.id}
                      onClick={() => setDrawerLead(l)}
                      className={`cursor-pointer transition-colors hover:bg-[#22C55E]/[0.04] ${overdue ? "bg-[#EF4444]/[0.06]" : dueToday ? "bg-[#FACC15]/[0.05]" : ""}`}
                      data-testid={`lead-row-${idx}`}
                    >
                      <td className="px-3 py-3 border-b border-[#17231B]" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={selected.has(l.id)} onChange={() => toggleSelect(l.id)} className="w-4 h-4 accent-[#22C55E]" data-testid={`lead-checkbox-${idx}`} aria-label={`Select ${l.name || "lead"}`} />
                      </td>
                      <td className="px-3 py-3 border-b border-[#17231B] sticky left-0 bg-[#0B110D] whitespace-nowrap">
                        <span className="font-semibold text-white">{l.name || <span className="text-[#6B7A70] font-normal">(no name)</span>}</span>
                        {l.test_data && <span className="ml-2 rounded-full bg-[#FACC15]/15 border border-[#FACC15]/40 text-[#FACC15] text-[10px] font-bold px-2 py-0.5" data-testid={`test-badge-${idx}`}>TEST</span>}
                        {l.form_state === "partial" && <span className="ml-2 rounded-full bg-white/5 border border-white/15 text-[#A7B3AA] text-[10px] font-bold px-2 py-0.5" data-testid={`partial-badge-${idx}`}>Partial</span>}
                      </td>
                      <td className="px-3 py-3 border-b border-[#17231B] text-[#6B7A70] whitespace-nowrap text-xs">{fmtDate(l.created_at)}</td>
                      <td className="px-3 py-3 border-b border-[#17231B] text-[#A7B3AA] whitespace-nowrap">{l.phone ? `+91 ${l.phone}` : "—"}</td>
                      <td className="px-3 py-3 border-b border-[#17231B] text-[#A7B3AA] max-w-[160px] truncate">{l.email || "—"}</td>
                      <td className="px-3 py-3 border-b border-[#17231B] text-[#A7B3AA]">{l.city || "—"}</td>
                      <td className="px-3 py-3 border-b border-[#17231B] text-[#A7B3AA] max-w-[170px]">{l.experience || "—"}</td>
                      <td className="px-3 py-3 border-b border-[#17231B] min-w-[200px]">
                        <div className="flex flex-wrap gap-1">
                          {(l.problems || []).length ? l.problems.map((p) => (
                            <span key={p} className="inline-block rounded-full bg-[#FACC15]/10 border border-[#FACC15]/30 text-[#FACC15] text-[10px] font-semibold px-2 py-0.5 whitespace-nowrap">{p}</span>
                          )) : <span className="text-[#6B7A70]">—</span>}
                        </div>
                      </td>
                      <td className="px-3 py-3 border-b border-[#17231B] text-[#A7B3AA] max-w-[150px]">{(l.markets || []).join(", ") || "—"}</td>
                      <td className="px-3 py-3 border-b border-[#17231B] text-[#A7B3AA] whitespace-nowrap">{l.mode || "—"}</td>
                      <td className="px-3 py-3 border-b border-[#17231B] text-[#A7B3AA] whitespace-nowrap">{l.timing || "—"}</td>
                      <td className="px-3 py-3 border-b border-[#17231B] text-[#A7B3AA] whitespace-nowrap">{l.capital || "—"}</td>
                      <td className="px-3 py-3 border-b border-[#17231B] text-[#A7B3AA] whitespace-nowrap text-xs">{l.utm_source || "Direct"}{l.utm_campaign ? ` / ${l.utm_campaign}` : ""}</td>
                      <td className="px-3 py-3 border-b border-[#17231B]">
                        <InlineSelect
                          value={l.status}
                          onChange={(v) => patch(l.id, { status: v }).catch((e) => toast.error(e.message))}
                          options={CRM.statuses}
                          className={STATUS_STYLE[l.status] || STATUS_STYLE.warm}
                          testid={`status-select-${idx}`}
                        />
                      </td>
                      <td className="px-3 py-3 border-b border-[#17231B]">
                        <InlineSelect
                          value={l.stage}
                          onChange={(v) => patch(l.id, { stage: v }).catch((e) => toast.error(e.message))}
                          options={CRM.stages.map((s) => ({ id: s.id, label: s.id }))}
                          className={STAGE_TONE_STYLE[stageTone(l.stage)]}
                          testid={`stage-select-${idx}`}
                        />
                      </td>
                      <td className="px-3 py-3 border-b border-[#17231B]" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="date"
                          value={l.next_follow_up || ""}
                          onChange={(e) => patch(l.id, { next_follow_up: e.target.value }).catch((err) => toast.error(err.message))}
                          data-testid={`followup-input-${idx}`}
                          className={`rounded-lg bg-[#070A08] border outline-none px-2 py-1.5 text-xs transition-colors ${overdue ? "border-[#EF4444]/50 text-[#EF4444]" : dueToday ? "border-[#FACC15]/50 text-[#FACC15]" : "border-[#22C55E]/[0.15] text-[#A7B3AA] focus:border-[#22C55E]/50"}`}
                        />
                      </td>
                      <td className="px-3 py-3 border-b border-[#17231B]"><NoteCell lead={l} onSave={patch} /></td>
                      <td className="px-3 py-3 border-b border-[#17231B] whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          {l.phone && (
                            <>
                              <a href={`tel:+91${l.phone}`} data-testid={`action-call-${idx}`} aria-label="Call" className="w-8 h-8 rounded-full border border-[#22C55E]/25 flex items-center justify-center text-[#22C55E] hover:bg-[#22C55E]/10 transition-colors">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                              </a>
                              <a href={leadWhatsAppLink(l.phone, CRM.followupWhatsApp.replace("{name}", l.name || "there"))} target="_blank" rel="noopener noreferrer" data-testid={`action-whatsapp-${idx}`} aria-label="WhatsApp" className="w-8 h-8 rounded-full border border-[#22C55E]/25 flex items-center justify-center text-[#22C55E] hover:bg-[#22C55E]/10 transition-colors">
                                <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5"><path d="M17.5 14.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.5 0 1.47 1.07 2.9 1.22 3.1.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.7.63.71.22 1.36.19 1.87.11.57-.08 1.76-.72 2-1.41.25-.7.25-1.29.18-1.41-.08-.13-.28-.2-.58-.35M12.05 21.79h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.86 9.86 0 0 1-1.51-5.26c0-5.45 4.44-9.88 9.9-9.88a9.83 9.83 0 0 1 9.88 9.89c0 5.45-4.44 9.88-9.89 9.88M20.5 3.49A11.8 11.8 0 0 0 12.05 0C5.5 0 .16 5.34.16 11.9c0 2.1.55 4.14 1.59 5.95L.06 24l6.3-1.65a11.88 11.88 0 0 0 5.68 1.45h.01c6.55 0 11.89-5.34 11.89-11.9 0-3.18-1.24-6.16-3.44-8.41"/></svg>
                              </a>
                              <button
                                onClick={() => { navigator.clipboard.writeText(`+91 ${l.phone}`); toast.success("Number copied"); }}
                                data-testid={`action-copy-${idx}`}
                                aria-label="Copy number"
                                className="w-8 h-8 rounded-full border border-[#22C55E]/25 flex items-center justify-center text-[#A7B3AA] hover:text-white hover:bg-[#22C55E]/10 transition-colors"
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {drawerLead && (
        <LeadDrawer
          lead={drawerLead}
          onClose={() => setDrawerLead(null)}
          onUpdated={updateLocal}
        />
      )}
    </div>
  );
}
